"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Ticket } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { fetchPackagesForSale, sellPackage } from "@/lib/services/abonementService"
import { saleAccounts, usesAfterSale } from "@/lib/appointment/abonements"
import { usePackageUses } from "./package-pay-select"
import { formatCurrency } from "@/lib/utils"

/**
 * Sell a package to the appointment's client from the payment section (option a).
 * The sale happens at once — the same sale as the client card's: the package, and a
 * client payment in "Package sales" that is not part of this visit's payment. Then
 * the package is pre-selected on the lines it can pay (unless the visit is already
 * paid); saving the appointment takes the visit.
 */
export function SellPackageButton({ preselect = true, className }: { preselect?: boolean; className?: string }) {
  const { t } = useTranslation()
  const { appointment } = useAppointmentModal()
  const [open, setOpen] = useState(false)
  const serviceIds = (Array.isArray(appointment?.services) ? appointment!.services : []).map((s: any) => Number(s.id))
  const hasClient = !!(appointment as any)?.client?.id
  const { data: packages } = useQuery({
    queryKey: ["packagesForSale", serviceIds.join(",")],
    queryFn: () => fetchPackagesForSale(serviceIds),
    enabled: !!appointment?.id && hasClient,
    staleTime: 60 * 1000,
  })
  if (!appointment?.id || !hasClient || !packages?.length) return null

  return (
    <>
      <button
        type="button"
        className={className ?? "inline-flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700"}
        onClick={() => setOpen(true)}
      >
        <Ticket className="h-3.5 w-3.5" aria-hidden="true" />
        {t("appointment.payment.sell_package", "Sell a package")}
      </button>
      {open && <SellPackageDialog packages={packages} preselect={preselect} onClose={() => setOpen(false)} />}
    </>
  )
}

function SellPackageDialog({ packages, preselect, onClose }: {
  packages: Awaited<ReturnType<typeof fetchPackagesForSale>>
  preselect: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { appointment, updateAppointment } = useAppointmentModal()
  const { uses } = usePackageUses()
  const accounts = useMemo(() => saleAccounts(appointment?.paymentSection?.paymentMethods as any), [appointment])
  const [typeId, setTypeId] = useState<number>(packages[0]?.id)
  const chosen = packages.find((p) => p.id === typeId) ?? packages[0]
  const [amount, setAmount] = useState<string>(String(chosen?.price ?? ""))
  const [accountId, setAccountId] = useState<number | null>(accounts[0]?.id ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setAmount(String(chosen?.price ?? "")) }, [typeId])   // a new package → its price

  const serviceIds = (Array.isArray(appointment?.services) ? appointment!.services : []).map((s: any) => Number(s.id))

  const submit = async () => {
    if (!appointment?.id || !chosen) return
    setBusy(true)
    setError(null)
    try {
      const sale = await sellPackage({
        record_id: Number(appointment.id), type_id: chosen.id, account_id: accountId,
        amount, service_ids: serviceIds,
      })
      await queryClient.invalidateQueries({ queryKey: ["recordAbonements", Number(appointment.id)] })
      if (preselect && sale.lines?.length) {
        updateAppointment({ paymentSection: { abonementUses: usesAfterSale(uses, sale) } } as any)
      }
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || t("appointment.payment.sell_failed", "Could not sell the package"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("appointment.payment.sell_package", "Sell a package")}</DialogTitle>
          <DialogDescription>
            {t("appointment.payment.sell_package_hint",
              "The client pays for the package now; it is recorded as a package sale, not as this visit’s payment. Then pay today’s service with it.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs text-gray-500">{t("appointment.payment.package_label", "Package")}</span>
            <select className="w-full rounded border border-gray-300 bg-white px-2 py-2" value={chosen?.id}
              onChange={(e) => setTypeId(Number(e.target.value))}>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} · {formatCurrency(p.price)}
                  {p.covers.length ? "" : ` · ${t("appointment.payment.package_other_services", "other services")}`}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">{t("appointment.payment.package_paid", "Paid")}</span>
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">{t("appointment.payment.package_account", "Paid into")}</span>
              <select className="w-full rounded border border-gray-300 bg-white px-2 py-2" value={accountId ?? ""}
                onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </label>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t("common.cancel", "Cancel")}</Button>
          <Button onClick={submit} disabled={busy || !chosen}>
            {busy ? t("appointment.payment.selling", "Selling…") : t("appointment.payment.sell_confirm", "Sell")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
